import { Component } from '@theme/component';

/**
 * `<results-switcher>` — an ARIA tablist that toggles result panels. One tab is
 * active at a time; Arrow/Home/End keys move focus and selection between tabs.
 *
 * Progressive enhancement: the first panel is rendered visible and the rest carry
 * the `hidden` attribute server-side, so with JS off the default panel still shows.
 *
 * @extends {Component}
 */
class ResultsSwitcher extends Component {
  /** Activate the clicked tab. `on:click` sits on each tab, so event.target is it. */
  select = (event) => {
    const tab = event.target;
    if (tab instanceof HTMLElement) this.#activate(tab);
  };

  /** Roving-tabindex keyboard navigation across the tabs. */
  navigate = (event) => {
    const { key } = event;
    if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') return;

    const tabs = this.#tabs;
    const current = tabs.indexOf(event.target);
    if (current === -1) return;

    event.preventDefault();
    let next = current;
    if (key === 'Home') next = 0;
    else if (key === 'End') next = tabs.length - 1;
    else next = (current + (key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;

    tabs[next].focus();
    this.#activate(tabs[next]);
  };

  /** @returns {HTMLElement[]} */
  get #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /** @param {HTMLElement} tab */
  #activate(tab) {
    const controls = tab.getAttribute('aria-controls');

    const update = () => {
      for (const t of this.#tabs) {
        const selected = t === tab;
        t.setAttribute('aria-selected', String(selected));
        t.tabIndex = selected ? 0 : -1;
      }
      for (const panel of this.querySelectorAll('[role="tabpanel"]')) {
        panel.toggleAttribute('hidden', panel.id !== controls);
      }
    };

    // Cross-fade the panel swap via a view transition (same approach as
    // shop-filter.js). The theme disables the root transition, so we opt in by
    // naming the panels element for the duration of the swap.
    const panels = this.refs.panels;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (typeof document.startViewTransition === 'function' && !reduceMotion && panels instanceof HTMLElement) {
      panels.style.viewTransitionName = 'results-preview-panels';
      const transition = document.startViewTransition(update);
      transition.finished.finally(() => {
        panels.style.viewTransitionName = '';
      });
    } else {
      update();
    }
  }
}

if (!customElements.get('results-switcher')) {
  customElements.define('results-switcher', ResultsSwitcher);
}
